// @flow

import React, { useCallback, useEffect, useMemo } from 'react';
import type { Node } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { FavoriteIcon } from '@riseart/wishlist';
import { selectVisitorId } from 'shared_services/redux/selectors/visitor';
import { selectStoreCode } from 'shared_services/redux/selectors/storeCode';
import { selectFavoriteByArtId } from 'shared_services/redux/selectors/favorites';
import {
  addFavorite as addFavoriteActionCreator,
  removeFavorite as removeFavoriteActionCreator,
} from 'shared_services/redux/actions/favorites/favorites';
import CREATE_MUTATION from 'shared_data/queries/favorites/create.graphql';
import DELETE_MUTATION from 'shared_data/queries/favorites/delete.graphql';

type Props = {
  artId: string | number,
  onClick?: Function,
  onLoading?: Function,
  onAfterFavorite?: Function,
  onAfterUnfavorite?: Function,
  refetchQueries?: Object[],
  children?: Node | Function,
  animated?: boolean,
  position?: string,
  type?: string,
  size?: string,
  width?: string,
  showText?: boolean,
  className?: string,
};

/**
 * FavoriteButton
 *
 * @param {Props} props
 */
export function FavoriteButton({
  artId: artIdProp,
  onClick,
  onLoading,
  onAfterFavorite,
  onAfterUnfavorite,
  refetchQueries,
  animated = true,
  children,
  position,
  type,
  size,
  width,
  showText = false,
  className,
}: Props): Node {
  const dispatch: Function = useDispatch();
  const artId = useMemo(() => parseInt(artIdProp, 10), [artIdProp]);

  // Get data from state
  const visitorId = useSelector(selectVisitorId);
  const storeCode = useSelector(selectStoreCode);
  const favoriteId = useSelector(selectFavoriteByArtId(artId));

  // Calculated values
  const isFavorited = useMemo(() => !!favoriteId, [favoriteId]);

  // Mutations
  const [addFavorite, { loading: addLoading }] = useMutation(CREATE_MUTATION, {
    variables: {
      visitorId,
      artId,
      store: storeCode,
    },
    ...(refetchQueries ? { refetchQueries } : {}),
  });
  const [removeFavorite, { loading: removeLoading }] = useMutation(DELETE_MUTATION, {
    variables: { id: favoriteId, store: storeCode },
    ...(refetchQueries ? { refetchQueries } : {}),
  });

  const isLoading = useMemo(() => addLoading || removeLoading, [addLoading, removeLoading]);

  // Set loading
  useEffect(() => {
    if (onLoading && typeof onLoading === 'function') {
      onLoading(isLoading);
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actions
  const actionAddFavorite = useCallback(
    (data: Object) => {
      dispatch(addFavoriteActionCreator(data));
    },
    [dispatch],
  );
  const actionRemoveFavorite = useCallback(
    (data: Object) => {
      dispatch(removeFavoriteActionCreator(data));
    },
    [dispatch],
  );

  /**
   * handleAfterUpdate
   *
   * @param {boolean} wasFavorited
   */
  const handleAfterUpdate = (wasFavorited: boolean) => {
    if (wasFavorited) {
      if (onAfterFavorite && typeof onAfterFavorite === 'function') {
        onAfterFavorite();
      }
    } else if (onAfterUnfavorite && typeof onAfterUnfavorite === 'function') {
      onAfterUnfavorite();
    }
  };

  /**
   * handleClick
   */
  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick({ isFavorited, addFavorite });
    } else if (isFavorited) {
      // Delete favorite
      removeFavorite(visitorId, favoriteId).then((result) => {
        handleAfterUpdate(!isFavorited);
        actionRemoveFavorite(result.data.deleteFavorite);
      });
    } else {
      // Create favorite
      addFavorite(visitorId, artId).then((result) => {
        handleAfterUpdate(!isFavorited);
        actionAddFavorite(result.data.createFavorite);
      });
    }
  };

  return children && typeof children === 'function' ? (
    children({ isFavorited, isLoading, onClick: handleClick })
  ) : (
    <FormattedMessage id={isFavorited ? 'common.removeFromWishlist' : 'common.addToWishlist'}>
      {(text: string) => (
        <FavoriteIcon
          animated={animated}
          type={type}
          size={size}
          isFavorited={isFavorited}
          isLoading={isLoading}
          onClick={handleClick}
          position={position}
          className={className}
          buttonProps={{
            title: text,
            'aria-label': text,
            width,
          }}
        >
          {children || (showText && text)}
        </FavoriteIcon>
      )}
    </FormattedMessage>
  );
}
